import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type OAuthProvider = 'google' | 'apple' | 'github';

export interface SupabaseAuthOptions {
    supabaseUrl: string;
    supabaseAnonKey: string;
    supabaseClient?: SupabaseClient;
    isDev?: boolean;
    skipAuth?: boolean;
}

export interface SupabaseAuthState {
    supabase: SupabaseClient;
    session: Session | null;
    user: User | null;
    loading: boolean;
    signInWithPassword: (email: string, password: string) => Promise<unknown>;
    signInWithOAuth: (provider: OAuthProvider, redirectTo?: string) => Promise<unknown>;
    signUp: (email: string, password: string) => Promise<unknown>;
    signOut: () => Promise<void>;
}

export function createSupabaseBrowserClient(
    supabaseUrl: string,
    supabaseAnonKey: string,
): SupabaseClient {
    return createClient(supabaseUrl, supabaseAnonKey);
}

export function createDevAuthSession(now = new Date()): { session: Session; user: User } {
    const user: User = {
        id: 'mock-user-id',
        email: 'dev@memorai.app',
        app_metadata: {},
        user_metadata: { full_name: 'Dev User' },
        aud: 'authenticated',
        created_at: now.toISOString(),
    };

    const session: Session = {
        access_token: 'mock-token',
        refresh_token: 'mock-refresh-token',
        expires_in: 3600,
        token_type: 'bearer',
        user,
    };

    return { session, user };
}

export async function getAccessToken(supabase: SupabaseClient): Promise<string | undefined> {
    const {
        data: { session },
    } = await supabase.auth.getSession();

    return session?.access_token;
}

export function useSupabaseAuth({
    supabaseUrl,
    supabaseAnonKey,
    supabaseClient,
    isDev = false,
    skipAuth = false,
}: SupabaseAuthOptions): SupabaseAuthState {
    const supabase = useMemo(
        () => supabaseClient ?? createSupabaseBrowserClient(supabaseUrl, supabaseAnonKey),
        [supabaseClient, supabaseUrl, supabaseAnonKey],
    );

    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isDev && skipAuth) {
            const devAuth = createDevAuthSession();
            setSession(devAuth.session);
            setUser(devAuth.user);
            setLoading(false);
            return;
        }

        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
            setSession(currentSession);
            setUser(currentSession?.user ?? null);
            setLoading(false);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession);
            setUser(nextSession?.user ?? null);
            setLoading(false);
        });

        return () => subscription.unsubscribe();
    }, [isDev, skipAuth, supabase]);

    const signInWithPassword = useCallback(
        async (email: string, password: string) =>
            supabase.auth.signInWithPassword({
                email,
                password,
            }),
        [supabase],
    );

    const signInWithOAuth = useCallback(
        async (provider: OAuthProvider, redirectTo?: string) =>
            supabase.auth.signInWithOAuth({
                provider,
                ...(redirectTo
                    ? {
                        options: {
                            redirectTo,
                        },
                    }
                    : {}),
            }),
        [supabase],
    );

    const signUp = useCallback(
        async (email: string, password: string) =>
            supabase.auth.signUp({
                email,
                password,
            }),
        [supabase],
    );

    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, [supabase]);

    return {
        supabase,
        session,
        user,
        loading,
        signInWithPassword,
        signInWithOAuth,
        signUp,
        signOut,
    };
}
